import type { TutorialPhase } from "../../stores/tutorialStore";

export type AdvanceMode = "button" | "interaction" | "auto";
export type StepPosition =
  | "center"
  | "top-left"
  | "top-right"
  | "bottom-center"
  | "bottom-left"
  | "bottom-right";
export type InteractionType = "panOrZoom" | "enterSystem" | "selectPlanet" | "walletConnect";
export type CanvasRegion = "center" | "top" | "bottom";

export interface TutorialStepDef {
  id: number;
  phase: TutorialPhase;
  title: string;
  description: string;
  descriptionMobile?: string;
  position: StepPosition;
  targetSelector?: string;
  canvasRegion?: CanvasRegion;
  advanceMode: AdvanceMode;
  autoAdvanceMs?: number;
  interactionType?: InteractionType;
  buttonLabel: string;
  showBackButton: boolean;
  requiresTab?: "explore" | "shipyard" | "missions";
  isBlocking: boolean;
}

export const TUTORIAL_STEPS: TutorialStepDef[] = [
  // --- Phase 1: Welcome & Galaxy Navigation (steps 0-5) ---
  {
    id: 0,
    phase: "welcome",
    title: "Welcome to Voidex",
    description:
      "Explore real star systems, build a fleet of ships, and farm for VOID tokens. Let's show you around.",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Let's Go",
    showBackButton: false,
    requiresTab: "explore",
    isBlocking: true,
  },
  {
    id: 1,
    phase: "welcome",
    title: "The Galaxy Map",
    description:
      "Each glowing dot is a real star system. You're looking at thousands of stars from our cosmic neighborhood.",
    position: "bottom-center",
    canvasRegion: "center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    requiresTab: "explore",
    isBlocking: false,
  },
  {
    id: 2,
    phase: "welcome",
    title: "Navigate the Galaxy",
    description: "Drag to pan around the galaxy. Scroll to zoom in and out.",
    descriptionMobile: "Drag with one finger to pan. Pinch to zoom in and out.",
    position: "top-right",
    targetSelector: ".navbar-controls",
    advanceMode: "interaction",
    interactionType: "panOrZoom",
    buttonLabel: "Next",
    showBackButton: true,
    requiresTab: "explore",
    isBlocking: false,
  },
  {
    id: 3,
    phase: "welcome",
    title: "Enter a Star System",
    description: "Click any star to enter its solar system and see its planets.",
    descriptionMobile: "Tap any star to enter its solar system and see its planets.",
    position: "bottom-center",
    canvasRegion: "center",
    advanceMode: "interaction",
    interactionType: "enterSystem",
    buttonLabel: "Skip",
    showBackButton: true,
    requiresTab: "explore",
    isBlocking: false,
  },
  {
    id: 4,
    phase: "welcome",
    title: "Explore Planets",
    description:
      "You're inside a star system! Planets orbit the star. Click one to see its details in the sidebar.",
    descriptionMobile:
      "You're inside a star system! Planets orbit the star. Tap one to see its details.",
    position: "bottom-center",
    canvasRegion: "center",
    advanceMode: "interaction",
    interactionType: "selectPlanet",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 5,
    phase: "welcome",
    title: "Search Stars & Planets",
    description:
      "Use the Search bar to quickly find any star or planet by name. Press Cmd+K to open it.",
    descriptionMobile: "Use the Search button to quickly find any star or planet by name.",
    position: "top-left",
    targetSelector: ".search-trigger",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },

  // --- Phase 2: Wallet & Web3 Basics (steps 6-8) ---
  {
    id: 6,
    phase: "wallet",
    title: "Connect Your Wallet",
    description:
      "To buy ships and start farming, you'll need to connect a Web3 wallet. This is your on-chain identity.",
    position: "top-left",
    targetSelector: ".wallet-overlay",
    advanceMode: "interaction",
    interactionType: "walletConnect",
    buttonLabel: "Skip",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 7,
    phase: "wallet",
    title: "Base Sepolia Testnet",
    description:
      "Voidex runs on Base Sepolia testnet. Your wallet will prompt you to switch networks if needed.",
    position: "top-left",
    targetSelector: ".wallet-overlay",
    advanceMode: "button",
    buttonLabel: "Got it",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 8,
    phase: "wallet",
    title: "Approvals",
    description:
      'Some actions require "approvals" \u2014 one-time permissions for smart contracts to move tokens on your behalf. This is standard and secure.',
    position: "center",
    advanceMode: "button",
    buttonLabel: "Got it",
    showBackButton: true,
    isBlocking: false,
  },

  // --- Phase 3: Shipyard (steps 9-11) ---
  {
    id: 9,
    phase: "shipyard",
    title: "The Shipyard",
    description:
      "This is where you build your fleet. Ships are NFTs that you send on farming missions.",
    position: "bottom-center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    requiresTab: "shipyard",
    isBlocking: false,
  },
  {
    id: 10,
    phase: "shipyard",
    title: "Ship Tiers",
    description:
      "There are 3 tiers: Probe (cheap, lower rewards), Frigate (balanced), and Dreadnought (expensive, best odds). Each costs USDC.",
    position: "bottom-center",
    targetSelector: ".ship-buy-section__grid",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    requiresTab: "shipyard",
    isBlocking: false,
  },
  {
    id: 11,
    phase: "shipyard",
    title: "Buying Ships",
    description:
      "To buy: first approve USDC spending (one-time), then confirm the purchase. Your ships appear in your inventory below.",
    position: "bottom-center",
    advanceMode: "button",
    buttonLabel: "Got it",
    showBackButton: true,
    requiresTab: "shipyard",
    isBlocking: false,
  },

  // --- Phase 4: Farming Missions (steps 12-17) ---
  {
    id: 12,
    phase: "farming",
    title: "Start a Mission",
    description:
      'To farm, find a planet in the galaxy and click "Send Mission" in the sidebar details panel.',
    descriptionMobile:
      'To farm, find a planet in the galaxy and tap "Send Mission" in the details panel.',
    position: "center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 13,
    phase: "farming",
    title: "Zones & Rewards",
    description:
      "Each star system belongs to a Zone based on distance. Higher zones mean longer missions but bigger reward multipliers.",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 14,
    phase: "farming",
    title: "Select Your Ship",
    description:
      "In the mission wizard, pick which ship to send. Ships already on missions won't be available.",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 15,
    phase: "farming",
    title: "VRF Outcomes",
    description:
      "After launch, on-chain VRF (Verifiable Random Function) determines your mission outcome. It's provably fair randomness.",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Next",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 16,
    phase: "farming",
    title: "5 Possible Outcomes",
    description:
      "Poor (low), Standard (base), Rich (high), Jackpot (max)... or Destroyed \u2014 your ship can be permanently lost!",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Got it",
    showBackButton: true,
    isBlocking: false,
  },
  {
    id: 17,
    phase: "farming",
    title: "Track & Claim",
    description:
      "Track active missions in the Missions tab. When a mission completes, claim your reward points!",
    position: "bottom-center",
    targetSelector: ".game-tabs",
    advanceMode: "button",
    buttonLabel: "Done",
    showBackButton: true,
    requiresTab: "explore",
    isBlocking: false,
  },

  // --- Phase 5: Completion (step 18) ---
  {
    id: 18,
    phase: "completion",
    title: "You're Ready, Explorer",
    description:
      "Your explorer points accumulate from farming missions. After TGE, convert them to VOID tokens. Good luck out there!",
    position: "center",
    advanceMode: "button",
    buttonLabel: "Start Exploring",
    showBackButton: false,
    isBlocking: true,
  },
];

export const PHASE_LABELS: Record<TutorialPhase, string> = {
  welcome: "Navigation",
  wallet: "Wallet",
  shipyard: "Shipyard",
  farming: "Missions",
  completion: "Ready!",
  done: "Complete",
};

export const PHASE_ORDER: TutorialPhase[] = [
  "welcome",
  "wallet",
  "shipyard",
  "farming",
  "completion",
];
